import { z } from 'zod';
import { TEST_DATA_TYPES, TestDataColumnSchema, TestDataInlineEntrySchema } from './test-data';
import { RepoKnowledgeKnownFactSchema } from './repo-scenario-suggestions';

export const TEST_DATA_SUGGESTION_MAX_ITEMS = 24;
export const TEST_DATA_SUGGESTION_MAX_PER_SOURCE = 6;

export const TestDataSuggestionStatusSchema = z.enum(['draft', 'reviewed', 'rejected', 'converted']);
export type TestDataSuggestionStatus = z.infer<typeof TestDataSuggestionStatusSchema>;

export const TestDataSuggestionSourceKindSchema = z.enum(['repo_proposal', 'scenario_suggestion', 'scenario']);
export type TestDataSuggestionSourceKind = z.infer<typeof TestDataSuggestionSourceKindSchema>;

export const TestDataEvidenceKindSchema = z.enum(['known', 'inferred', 'assumption']);
export type TestDataEvidenceKind = z.infer<typeof TestDataEvidenceKindSchema>;

export const TestDataCatalogueMatchSchema = z.enum(['none', 'exact_key', 'partial_column']);
export type TestDataCatalogueMatch = z.infer<typeof TestDataCatalogueMatchSchema>;

export const TestDataSuggestionEvidenceSchema = z.object({
  label: z.string().min(1).max(120),
  value: z.string().max(500),
  kind: TestDataEvidenceKindSchema,
});
export type TestDataSuggestionEvidence = z.infer<typeof TestDataSuggestionEvidenceSchema>;

export const TestDataSuggestionEvaluationSchema = z.object({
  confidence: z.enum(['high', 'medium', 'low']),
  summary: z.string().max(500),
});
export type TestDataSuggestionEvaluation = z.infer<typeof TestDataSuggestionEvaluationSchema>;

export const TestDataSuggestionProvenanceSchema = z.object({
  generationSource: z.literal('heuristic'),
  sourceKind: TestDataSuggestionSourceKindSchema,
  sourceId: z.string().uuid(),
  sourceLabel: z.string().max(300),
});
export type TestDataSuggestionProvenance = z.infer<typeof TestDataSuggestionProvenanceSchema>;

export const TestDataSuggestionSchema = z.object({
  id: z.string().uuid(),
  projectId: z.string().uuid(),
  sourceKind: TestDataSuggestionSourceKindSchema,
  sourceId: z.string().uuid(),
  status: TestDataSuggestionStatusSchema,
  suggestedKey: z.string().min(1).max(80),
  suggestedName: z.string().min(1).max(200),
  suggestedType: z.enum(TEST_DATA_TYPES),
  suggestedCategory: z.string().max(120).default(''),
  suggestedColumns: z.array(TestDataColumnSchema).default([]),
  suggestedRows: z.array(z.record(z.string(), z.string())).default([]),
  suggestedInlineEntries: z.array(TestDataInlineEntrySchema).default([]),
  evidenceKind: TestDataEvidenceKindSchema,
  sourceLabel: z.string().max(300),
  evidence: z.array(TestDataSuggestionEvidenceSchema).max(20),
  knownFacts: z.array(RepoKnowledgeKnownFactSchema).max(20),
  assumptions: z.array(z.string().max(300)).max(20),
  existingCatalogueId: z.string().uuid().nullable().optional(),
  existingCatalogueMatch: TestDataCatalogueMatchSchema,
  evaluation: TestDataSuggestionEvaluationSchema.optional(),
  provenance: TestDataSuggestionProvenanceSchema,
  convertedCatalogueId: z.string().uuid().nullable().optional(),
  updatedAt: z.string(),
});
export type TestDataSuggestion = z.infer<typeof TestDataSuggestionSchema>;

export const ListTestDataSuggestionsResponseSchema = z.object({
  projectId: z.string().uuid(),
  suggestions: z.array(TestDataSuggestionSchema),
  openCount: z.number().int().min(0),
  recommendationsAreNotTruth: z.literal(true),
});
export type ListTestDataSuggestionsResponse = z.infer<typeof ListTestDataSuggestionsResponseSchema>;

export const GenerateTestDataSuggestionsRequestSchema = z.object({
  proposalIds: z.array(z.string().uuid()).max(TEST_DATA_SUGGESTION_MAX_ITEMS).optional(),
  includeScenarioSuggestions: z.boolean().optional(),
  includeScenarios: z.boolean().optional(),
});
export type GenerateTestDataSuggestionsRequest = z.infer<typeof GenerateTestDataSuggestionsRequestSchema>;

export const GenerateTestDataSuggestionsResponseSchema = z.object({
  projectId: z.string().uuid(),
  suggestions: z.array(TestDataSuggestionSchema),
  createdCount: z.number().int().min(0),
  skippedDuplicateCount: z.number().int().min(0),
  skippedExistingKeyCount: z.number().int().min(0),
  generationSource: z.literal('heuristic'),
  recommendationsAreNotTruth: z.literal(true),
});
export type GenerateTestDataSuggestionsResponse = z.infer<typeof GenerateTestDataSuggestionsResponseSchema>;

export const ReviewTestDataSuggestionRequestSchema = z.object({
  status: z.enum(['reviewed', 'rejected']),
});
export type ReviewTestDataSuggestionRequest = z.infer<typeof ReviewTestDataSuggestionRequestSchema>;

export const AcceptTestDataSuggestionResponseSchema = z.object({
  suggestion: TestDataSuggestionSchema,
  catalogueId: z.string().uuid(),
});
export type AcceptTestDataSuggestionResponse = z.infer<typeof AcceptTestDataSuggestionResponseSchema>;
